// ──────────────────────────────────────────────────────────────────────
// BUG-02 (2026-05-23) — Clock-in location requirement card.
// Workspace setting (Owner/Admin only) — four toggles, one per role.
// All default OFF. When OFF, navigator.geolocation is never called for
// that role's clock-in. When ON, the role is gated on GPS like before.
// ──────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';

const ROLES = [
  { id: 'owner',   label: 'Owner' },
  { id: 'admin',   label: 'Admin',   hint: 'Trusted operators.' },
  { id: 'manager', label: 'Manager', hint: 'Team leads.' },
  { id: 'team',    label: 'Member',  hint: 'Field / IC staff.' },
];

const API = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '') + '/api';

export default function ClockInLocationPolicyCard({ workspaceId }) {
  const [policy, setPolicy] = useState({ owner: false, admin: false, manager: false, team: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // role being saved
  // D64 P2-4 — accurate "Owner" hint. Show "You." only when the viewer
  // actually IS the owner; otherwise display the owner's real name.
  const { user } = useAuth();
  const { workspaceRole, currentWorkspace } = useWorkspace();
  const viewerIsOwner = (workspaceRole || user?.role) === 'owner' || (workspaceRole || user?.role) === 'super_admin';
  const ownerName = currentWorkspace?.owner_name || currentWorkspace?.name || 'Workspace owner';
  const token = localStorage.getItem('stellar_token');

  useEffect(() => {
    if (!workspaceId) return;
    setLoading(true);
    axios.get(`${API}/workspaces/${workspaceId}/settings/clock-in-location`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPolicy(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [workspaceId, token]);

  const toggle = async (role, next) => {
    setSaving(role);
    const optimistic = { ...policy, [role]: next };
    setPolicy(optimistic);
    try {
      const r = await axios.put(
        `${API}/workspaces/${workspaceId}/settings/clock-in-location`,
        optimistic,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
      );
      setPolicy(r.data);
      toast.success(`Location requirement ${next ? 'ON' : 'OFF'} for ${role}`);
    } catch (err) {
      setPolicy(policy); // rollback
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return <div className="text-center py-6 text-[12px] text-muted-foreground/50"><Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Loading clock-in policy…</div>;
  }

  return (
    <Card className="bg-card/60 backdrop-blur" data-testid="clock-in-location-policy-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-[13px] font-semibold flex items-center gap-2">
          <MapPin className="h-3.5 w-3.5 text-gold" /> Clock-In Requirements
        </CardTitle>
        <p className="text-[11px] text-muted-foreground/50">
          Require location capture when these roles clock in. All roles default OFF — flip ON when field tracking is needed.
        </p>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {ROLES.map(r => (
          <div
            key={r.id}
            className="flex items-center justify-between py-2 px-3 rounded-lg border border-border/20 bg-foreground/[0.01]"
            data-testid={`clock-loc-row-${r.id}`}
          >
            <div className="flex flex-col min-w-0">
              <span className="text-[13px] font-medium text-foreground">Require location · {r.label}</span>
              <span className="text-[11px] text-muted-foreground/50">{r.id === 'owner' ? (viewerIsOwner ? 'You.' : ownerName) : r.hint}</span>
            </div>
            <div className="flex items-center gap-2">
              {saving === r.id && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground/60" />}
              <Switch
                checked={!!policy[r.id]}
                onCheckedChange={(v) => toggle(r.id, v)}
                disabled={saving === r.id}
                data-testid={`clock-loc-toggle-${r.id}`}
              />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
